/**
 * Everything this app asks the API about records.
 *
 * The only caller of `client` for this slice — routes and components go
 * through `queries.ts`, never straight to `fetch`.
 */

import { type Envelope, client } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";

export type MasterRecord = components["schemas"]["RecordOut"];
export type RecordPage = components["schemas"]["RecordPage"];
export type RecordEdit = components["schemas"]["RecordEditOut"];
export type RecordVersion = components["schemas"]["RecordVersionOut"];
export type RecordVersionPage = components["schemas"]["RecordVersionPage"];
export type HierarchyNode = components["schemas"]["HierarchyNodeOut"];
export type Submission = components["schemas"]["SubmissionOut"];
export type RecordCreate = components["schemas"]["RecordCreate"];
export type RecordUpdate = components["schemas"]["RecordUpdate"];

function base(code: string): string {
  return `/api/v1/master-types/${encodeURIComponent(code)}/records`;
}

export interface ListRecordsParams {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
  includeInactive?: boolean | undefined;
  /** The status a reader *sees*, matching `statusOf` — `approved` means
   *  approved and active, `inactive` means approved and deactivated. Not
   *  the raw column, so a pill and the badges under it agree (DEE-74). */
  status?: RecordStatusFilter | undefined;
  locale?: string | undefined;
  /** `{iso_code: "IN"}` becomes `filter[iso_code]=IN`, the `deepObject`
   *  shape `mdm.records.filtering.parse_filters` reads. */
  filters?: Record<string, string> | undefined;
}

/** Mirrors `mdm.records.filtering.RecordStatus`. Sourced from the generated
 *  schema so a value added server-side is a type error here rather than a
 *  pill that silently 422s. */
export type RecordStatusFilter = components["schemas"]["RecordStatus"];

export function listRecords(code: string, params: ListRecordsParams = {}): Promise<RecordPage> {
  const filters: Record<string, string> = {};
  for (const [field, value] of Object.entries(params.filters ?? {})) {
    if (value !== "") filters[`filter[${field}]`] = value;
  }
  return client.request<RecordPage>(base(code), {
    query: {
      cursor: params.cursor,
      limit: params.limit,
      sort: params.sort,
      q: params.q === "" ? undefined : params.q,
      include_inactive: params.includeInactive,
      status: params.status,
      locale: params.locale,
      ...filters,
    },
  });
}

export function getRecord(code: string, id: string, locale?: string): Promise<MasterRecord> {
  return client.request<MasterRecord>(`${base(code)}/${id}`, { query: { locale } });
}

export async function countRecords(code: string): Promise<number> {
  const { count } = await client.request<components["schemas"]["RecordCountOut"]>(
    `${base(code)}/count`,
  );
  return count;
}

/** The `limit` most recently created records of this type, newest first —
 *  the dashboard's per-type contribution to the "recently updated" feed. */
export function recentRecords(
  code: string,
  params: { limit?: number | undefined; locale?: string | undefined } = {},
): Promise<MasterRecord[]> {
  return client.request<MasterRecord[]>(`${base(code)}/recent`, {
    query: { limit: params.limit, locale: params.locale },
  });
}

/**
 * The record as an editor needs it — every locale, unresolved — plus the
 * ETag the following `PATCH` must send back.
 *
 * Never build an edit form on `getRecord`. It resolves each localised
 * field to one locale, and `PATCH` replaces the whole map, so writing
 * back what that returned deletes every other translation.
 */
export function getRecordForEdit(code: string, id: string): Promise<Envelope<RecordEdit>> {
  return client.requestWithEtag<RecordEdit>(`${base(code)}/${id}/edit`);
}

export function listVersions(
  code: string,
  id: string,
  params: { cursor?: string | undefined } = {},
): Promise<RecordVersionPage> {
  return client.request<RecordVersionPage>(`${base(code)}/${id}/versions`, {
    query: { cursor: params.cursor },
  });
}

export function childrenOf(
  code: string,
  id: string,
  params: { locale?: string | undefined; includeInactive?: boolean | undefined } = {},
): Promise<HierarchyNode[]> {
  return client.request<HierarchyNode[]>(`${base(code)}/${id}/children`, {
    query: { locale: params.locale, include_inactive: params.includeInactive },
  });
}

export function ancestorsOf(
  code: string,
  id: string,
  params: { locale?: string | undefined } = {},
): Promise<HierarchyNode[]> {
  return client.request<HierarchyNode[]>(`${base(code)}/${id}/ancestors`, {
    query: { locale: params.locale },
  });
}

/**
 * Create a draft — or amend an approved record.
 *
 * The same call does both, and that is the API's design rather than an
 * accident: posting a business key that already has an approved current
 * row opens the next version of it. `PATCH` only ever edits a draft that
 * is still open.
 */
export function createRecord(code: string, body: RecordCreate): Promise<MasterRecord> {
  return client.request<MasterRecord>(base(code), { method: "POST", body });
}

export function updateRecord(
  code: string,
  id: string,
  body: RecordUpdate,
  etag: string,
): Promise<MasterRecord> {
  return client.request<MasterRecord>(`${base(code)}/${id}`, {
    method: "PATCH",
    body,
    ifMatch: etag,
  });
}

export function submitRecord(code: string, id: string): Promise<Submission> {
  return client.request<Submission>(`${base(code)}/${id}/submit`, { method: "POST" });
}

export function deactivateRecord(code: string, id: string, cascade: boolean): Promise<Submission> {
  return client.request<Submission>(`${base(code)}/${id}/deactivate`, {
    method: "POST",
    query: { cascade },
  });
}

export function reactivateRecord(code: string, id: string): Promise<Submission> {
  return client.request<Submission>(`${base(code)}/${id}/reactivate`, { method: "POST" });
}
